<template>
  <MetalAccordion ref="root" sticky :open :group-open @toggle="onToggle">
    <template #header>
      <RevIllustration
        v-if="icon"
        :src="icon"
        :width="18"
        :height="18"
        alt=""
        aria-hidden="true"
        class="aspect-square object-cover"
      />
      <span class="raw-cta-2 text-left">
        {{ i18n(label) }}<FootnoteMarker :marker />
      </span>
      <ScoreCircles v-if="score !== null" class="ms-auto" :score />
      <span v-else class="raw-body-2 ms-auto shrink-0 opacity-65">
        {{ i18n(translations.notAssessed) }}
      </span>
    </template>
    <ContentBlocks :blocks="visibleBlocks" :zone />
    <button
      v-if="hasMore"
      type="button"
      class="raw-cta-3 justify-self-start underline"
      @click="toggleExpanded()"
    >
      {{ i18n(expanded ? translations.readLess : translations.readMore) }}
    </button>
  </MetalAccordion>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevIllustration } from '@ds/components/Illustration'

import FootnoteMarker from '~/scopes/campaign/components/UI/FootnoteMarker.vue'
import ContentBlocks from '~/scopes/campaign/components/highlights/metal/ContentBlocks.vue'
import MetalAccordion from '~/scopes/campaign/components/highlights/metal/MetalAccordion.vue'
import ScoreCircles from '~/scopes/campaign/components/highlights/metal/ScoreCircles.vue'
import type {
  I18nDescriptor,
  MetalBlock,
  Score5,
} from '~/scopes/campaign/data/metals/types'

import translations from './ScoredAccordion.translations'

// Every scored section of the metal sheet — the three impacts and the "Can we keep up?"
// pair — which differ only in the header icon.
const props = defineProps<{
  label: I18nDescriptor
  /** `null` renders the sheet's "Not assessed" in place of the five dots. */
  score: Score5 | null
  summary: MetalBlock
  blocks: readonly MetalBlock[]
  icon?: string
  /** Score footnote; rides the label, since a `<sup>` after five dots has no word to hold. */
  marker?: string
  /** Tracking slug: `impact-human`, `supply-risk`, `recycling`. */
  name: string
  open?: boolean
  groupOpen?: boolean
  /** Tracking surface of the host view. */
  zone: string
}>()
const emit = defineEmits<{ toggle: [open: boolean] }>()

const i18n = useI18n()
const { trackClick } = useTracking()
const route = useRoute()

const expanded = ref(false)
const root = ref<InstanceType<typeof MetalAccordion> | null>(null)

// Any close, self or by a sibling, reopens at preview length.
watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) expanded.value = false
  },
)

// The summary/blocks split is authored, so the reveal can't cut a section in half.
const visibleBlocks = computed(() =>
  expanded.value ? [props.summary, ...props.blocks] : [props.summary],
)
const hasMore = computed(() => props.blocks.length > 0)

function toggleExpanded() {
  expanded.value = !expanded.value
  trackClick({
    zone: props.zone,
    name: `${props.name}-${expanded.value ? 'read-more' : 'read-less'}`,
    label: i18n(props.label),
    campaign: 'raw-materials',
  })
  // Read-less takes a chunk out from above the reader just as a close does;
  // same fixed point, same call. Synchronous, so it measures the pre-shrink DOM.
  if (!expanded.value) root.value?.anchorHeader()
}

function onToggle(isOpen: boolean) {
  trackClick({
    zone: props.zone,
    name: `${props.name}-${isOpen ? 'open' : 'close'}`,
    label: i18n(props.label),
    campaign: 'raw-materials',
    routeQuery: route.query,
    routeParams: route.params,
  })
  // The owner enforces single-open across the group.
  emit('toggle', isOpen)
}
</script>
